import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard, 
  FileText, 
  AlertTriangle, 
  History,
  LogOut,
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Tag,
  Shield,
  Loader2,
  Users,
  Building,
  Globe,
  Pencil,
  RotateCcw,
  Ban,
  CheckCircle,
  Store,
  Gift,
  BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

const categoryNames = {
  disabled_blue_badge: "Disabled Blue Badge",
  adult_with_child: "Adult with Child in Vehicle",
  extra_wide: "Extra Wide Bay",
  nhs_staff: "NHS Staff",
  military: "Military / Ex-Military",
  lone_female: "Legacy Category"
};

export default function AdminRegistrationDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [sites, setSites] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showScopeDialog, setShowScopeDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Form states
  const [statusAction, setStatusAction] = useState(null);
  const [actionReason, setActionReason] = useState("");
  const [newScope, setNewScope] = useState("");
  const [scopeReason, setScopeReason] = useState("");
  const [editForm, setEditForm] = useState({ email: "", phone: "", full_name: "" });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      fetchData();
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const fetchData = async () => {
    try {
      const [regRes, sitesRes, logsRes] = await Promise.all([
        axios.get(`${API}/registrations/${id}`, { withCredentials: true }),
        axios.get(`${API}/sites`, { withCredentials: true }),
        axios.get(`${API}/audit-logs?entity_id=${id}`, { withCredentials: true })
      ]);
      setRegistration(regRes.data);
      setSites(sitesRes.data);
      setAuditLogs(logsRes.data);
      setEditForm({
        email: regRes.data.email || "",
        phone: regRes.data.phone || "",
        full_name: regRes.data.full_name || ""
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load registration");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!statusAction || !actionReason.trim()) {
      toast.error("Please provide a reason");
      return;
    }
    
    setProcessing(true);
    try {
      const response = await axios.patch(
        `${API}/registrations/${id}/status`,
        { status: statusAction, reason: actionReason },
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      setShowStatusDialog(false);
      setActionReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setProcessing(false);
    }
  };

  const handleScopeChange = async () => {
    if (!newScope || !scopeReason.trim()) {
      toast.error("Please provide a reason for scope change");
      return;
    }
    
    setProcessing(true);
    try {
      await axios.patch(
        `${API}/registrations/${id}/scope`,
        { entitlement_scope: newScope, reason: scopeReason },
        { withCredentials: true }
      );
      
      toast.success("Entitlement scope updated");
      setShowScopeDialog(false);
      setScopeReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update scope");
    } finally {
      setProcessing(false);
    }
  };

  const handleDetailsUpdate = async () => {
    setProcessing(true);
    try {
      await axios.patch(
        `${API}/registrations/${id}/details`,
        editForm,
        { withCredentials: true }
      );
      
      toast.success("Registration details updated");
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update details");
    } finally {
      setProcessing(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {}
    navigate("/admin/login");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.site_id === siteId);
    return site ? site.name : siteId || "Unknown";
  };

  const getStatusBadge = (status) => {
    const classes = {
      active: "status-badge status-active",
      expired: "status-badge status-expired",
      suspended: "status-badge status-suspended",
      revoked: "status-badge status-revoked"
    };
    return <span className={classes[status] || "status-badge"}>{status}</span>;
  };

  const openStatusDialog = (action) => {
    setStatusAction(action);
    setActionReason("");
    setShowStatusDialog(true);
  };

  const openScopeDialog = () => {
    setNewScope(registration?.entitlement_scope === "site_only" ? "all_sites" : "site_only");
    setScopeReason("");
    setShowScopeDialog(true);
  };

  if (loading) {
    return (
      <div className="admin-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  const isExpired = registration?.expires_at && new Date(registration.expires_at) < new Date();

  return (
    <div className="admin-container min-h-screen" data-testid="admin-registration-detail">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
            <FileText className="w-5 h-5" />
            <span>Registrations</span>
          </Link>
          <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Building className="w-5 h-5" />
            <span>Sites & Kiosks</span>
          </Link>
          <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Store className="w-5 h-5" />
            <span>Retailers</span>
          </Link>
          <Link to="/admin/rewards" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Gift className="w-5 h-5" />
            <span>Rewards</span>
          </Link>
          <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <BarChart3 className="w-5 h-5" />
            <span>Analytics</span>
          </Link>
          <Link to="/admin/ocr-exceptions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <AlertTriangle className="w-5 h-5" />
            <span>OCR Exceptions</span>
          </Link>
          <Link to="/admin/audit-log" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <History className="w-5 h-5" />
            <span>Audit Log</span>
          </Link>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full">
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        <Button variant="ghost" onClick={() => navigate("/admin/registrations")} className="mb-6 text-slate-400 hover:text-white" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Registrations
        </Button>

        {registration && (
          <>
            {/* Header */}
            <div className="flex items-start justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {registration.vehicle_registration}
                </h1>
                <p className="text-slate-400">{categoryNames[registration.category] || registration.category}</p>
                
                {/* Origin Site - Permanent, Non-Editable Display */}
                <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-slate-800/60 border border-slate-700 rounded-lg" data-testid="origin-site-header">
                  <Building className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-slate-400">Origin Site:</span>
                  <span className="text-sm font-medium text-amber-300">
                    {getSiteName(registration.origin_site_id || registration.site_id)}
                  </span>
                  <span className="text-xs text-slate-500 ml-2">(Permanent)</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {getStatusBadge(registration.status)}
                
                {/* Status Actions */}
                {registration.status === "active" && (
                  <>
                    <Button onClick={() => openStatusDialog("suspended")} variant="outline" className="border-orange-500 text-orange-400 hover:bg-orange-500/10">
                      <Ban className="w-4 h-4 mr-2" />
                      Suspend
                    </Button>
                    <Button onClick={() => openStatusDialog("revoked")} variant="outline" className="border-red-500 text-red-400 hover:bg-red-500/10">
                      <Ban className="w-4 h-4 mr-2" />
                      Revoke
                    </Button>
                  </>
                )}
                {(registration.status === "suspended" || registration.status === "revoked") && !isExpired && (
                  <Button onClick={() => openStatusDialog("active")} variant="outline" className="border-green-500 text-green-400 hover:bg-green-500/10">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reactivate
                  </Button>
                )}
              </div>
            </div>

            {/* Validity Indicator */}
            <div className={`mb-6 p-4 rounded-xl border ${
              registration.status === "active" && !isExpired
                ? registration.entitlement_scope === "all_sites"
                  ? "bg-blue-500/10 border-blue-500/30"
                  : "bg-green-500/10 border-green-500/30"
                : "bg-slate-800 border-slate-700"
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {registration.entitlement_scope === "all_sites" ? (
                    <Globe className="w-6 h-6 text-blue-400" />
                  ) : (
                    <MapPin className="w-6 h-6 text-green-400" />
                  )}
                  <div>
                    <p className="font-medium text-white">
                      {registration.entitlement_scope === "all_sites" 
                        ? "Valid at All Sites"
                        : `Valid at ${getSiteName(registration.origin_site_id || registration.site_id)} Only`
                      }
                    </p>
                    <p className="text-sm text-slate-400">
                      Origin Site: {getSiteName(registration.origin_site_id || registration.site_id)}
                    </p>
                  </div>
                </div>
                {registration.status === "active" && (
                  <Button onClick={openScopeDialog} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                    <Pencil className="w-4 h-4 mr-2" />
                    Change Scope
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Personal Details */}
              <Card className="admin-card border-slate-800">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-400" />
                    Personal Details
                  </CardTitle>
                  <Button onClick={() => setShowEditDialog(true)} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                    <Pencil className="w-4 h-4" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-500">Full Name</p>
                      <p className="text-white">{registration.full_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="text-white">{registration.email}</p>
                    </div>
                  </div>
                  {registration.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="text-sm text-slate-500">Phone</p>
                        <p className="text-white">{registration.phone}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Registration Details */}
              <Card className="admin-card border-slate-800">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Tag className="w-5 h-5 text-green-400" />
                    Registration Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-500">Origin Site / Kiosk</p>
                      <p className="text-white">{getSiteName(registration.origin_site_id || registration.site_id)} / {registration.kiosk_id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-500">Registered</p>
                      <p className="text-white">{formatDate(registration.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-500">Expires</p>
                      <p className={`${isExpired ? 'text-red-400' : 'text-white'}`}>
                        {registration.expires_at ? formatDate(registration.expires_at) : "Indefinite"}
                        {isExpired && " (EXPIRED)"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Blue Badge Data */}
              {registration.blue_badge_data && (
                <Card className="admin-card border-slate-800 lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-400" />
                      Blue Badge Information
                      {registration.ocr_status && (
                        <span className={`ml-2 text-xs px-2 py-1 rounded ${
                          registration.ocr_status === 'completed' ? 'bg-green-500/20 text-green-400' : 
                          registration.ocr_status === 'manual_review' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          OCR: {registration.ocr_status}
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Badge Number</p>
                        <p className="text-white font-mono">{registration.blue_badge_data.badge_number || "—"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Holder Name</p>
                        <p className="text-white">{registration.blue_badge_data.holder_name || "—"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Expiry Date</p>
                        <p className="text-white">{registration.blue_badge_data.expiry_date || "—"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Issuing Authority</p>
                        <p className="text-white">{registration.blue_badge_data.issuing_authority || "—"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Audit Trail */}
              <Card className="admin-card border-slate-800 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <History className="w-5 h-5 text-purple-400" />
                    Audit Trail
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {auditLogs.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No audit history</p>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {auditLogs.map((log, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                          <div className="w-2 h-2 mt-2 rounded-full bg-blue-400"></div>
                          <div className="flex-1">
                            <p className="text-sm text-white">
                              <span className="font-medium">{log.action.replace(/_/g, ' ')}</span>
                              {log.details && (
                                <span className="text-slate-400 ml-2">
                                  {log.details.reason && `— ${log.details.reason}`}
                                  {log.details.old_scope && log.details.new_scope && 
                                    ` (${log.details.old_scope} → ${log.details.new_scope})`}
                                  {log.details.old_status && log.details.new_status && 
                                    ` (${log.details.old_status} → ${log.details.new_status})`}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-slate-500">{formatDate(log.timestamp)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </main>

      {/* Status Change Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {statusAction === "active" ? "Reactivate Registration" : 
               statusAction === "suspended" ? "Suspend Registration" : "Revoke Registration"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {statusAction === "active" 
                ? "This will restore the registration to active status with its previous entitlement scope."
                : "This action will be recorded in the audit log."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label className="text-slate-300">Reason *</Label>
              <Textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Provide a reason for this action..."
                className="bg-slate-800 border-slate-700 text-white mt-2"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setShowStatusDialog(false)} variant="outline" className="flex-1 border-slate-600 text-slate-300">
                Cancel
              </Button>
              <Button 
                onClick={handleStatusChange} 
                disabled={processing || !actionReason.trim()}
                className={`flex-1 ${
                  statusAction === "active" ? "bg-green-600 hover:bg-green-700" :
                  statusAction === "suspended" ? "bg-orange-600 hover:bg-orange-700" :
                  "bg-red-600 hover:bg-red-700"
                }`}
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scope Change Dialog */}
      <Dialog open={showScopeDialog} onOpenChange={setShowScopeDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Change Entitlement Scope</DialogTitle>
            <DialogDescription className="text-slate-400">
              This will change where the registration is valid. The origin site remains unchanged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label className="text-slate-300">New Scope</Label>
              <Select value={newScope} onValueChange={setNewScope}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="site_only" className="text-white">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Site Only — Valid at origin site only
                    </div>
                  </SelectItem>
                  <SelectItem value="all_sites" className="text-white">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      All Sites — Valid at all sites
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Reason *</Label>
              <Textarea
                value={scopeReason}
                onChange={(e) => setScopeReason(e.target.value)}
                placeholder="Provide a reason for this scope change..."
                className="bg-slate-800 border-slate-700 text-white mt-2"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setShowScopeDialog(false)} variant="outline" className="flex-1 border-slate-600 text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleScopeChange} disabled={processing || !scopeReason.trim()} className="flex-1 bg-blue-600 hover:bg-blue-700">
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Scope"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Details Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Registration Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label className="text-slate-300">Full Name</Label>
              <Input
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white mt-2"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white mt-2"
              />
            </div>
            <div>
              <Label className="text-slate-300">Phone</Label>
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white mt-2"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setShowEditDialog(false)} variant="outline" className="flex-1 border-slate-600 text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleDetailsUpdate} disabled={processing} className="flex-1 bg-blue-600 hover:bg-blue-700">
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
